import { Button, Group, MultiSelect, Stack, TextInput } from '@mantine/core'
import { Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetSponsorTopicsResponse } from '@/services/api/api.types'
import { type GetUsersResponseEntry } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

import type { AllowedUserRole } from './AddUserModal'
import type { UserFormValues } from './types'
import { validateEmail, validatePassword } from './utils'

interface EditUserModalProps {
  closeModal: () => void
  user: GetUsersResponseEntry
  topics: GetSponsorTopicsResponse
  role?: AllowedUserRole
  entityName: string
}

interface UpdateUserResponse {
  success?: boolean
  errors?: unknown
}

export function EditUserModal({ closeModal, topics, user, role, entityName }: EditUserModalProps) {
  const { api, uiStore } = useGlobalState()
  const [isProcessing, setIsProcessing] = useState(false)

  const canSelectRole = role == undefined
  const showTopics = role ? role === 'SME' : true

  const topicOptions = topics.map(topic => ({
    value: topic.id,
    label: topic.displayName,
  }))

  // Determine user's current role based on their permissions
  const getUserRole = (): UserFormValues['role'] => {
    if (user.isSponsorAdmin) return 'Admin'
    if (user.smeAccess) return 'SME'
    return 'User'
  }

  const form = useForm<UserFormValues>({
    initialValues: {
      name: user.name || '',
      email: user.email,
      password: '',
      role: getUserRole(),
      topicIds: user.topicIds || [],
    },
    validate: {
      email: validateEmail,
      password: validatePassword,
      topicIds: (value: string[] | undefined) => {
        // If they are a SME, they must have at least one topic
        if (form.values.role === 'SME' && value?.length === 0) return 'At least one topic is required'
        return null
      },
    },
  })

  const updateUserMutation = useMutation<
    UpdateUserResponse,
    Error,
    {
      email: string
      user_id: string
      name?: string
      sme: boolean
      is_user_disabled: boolean
      sponsor_admin: boolean
      password?: string
      topic_ids?: string[]
    }
  >({
    mutationFn: api.updateUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: response => {
      const success = 'success' in response && response.success
      const error = 'errors' in response
      if (success) {
        api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        closeModal()
        notifications.show({
          title: 'Success',
          message: `${entityName} updated successfully`,
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else if (error) {
        console.log('error', response)
        notifications.show({
          title: 'Error',
          message: `Failed to update ${entityName}. Please try again.`,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
      setIsProcessing(false)
    },
  })

  const handleSubmit = (values: UserFormValues) => {
    setIsProcessing(true)
    updateUserMutation.mutate({
      email: values.email,
      user_id: user.userId,
      name: values.name,
      sme: values.role === 'SME',
      sponsor_admin: values.role === 'Admin',
      is_user_disabled: user.isUserDisabled,
      topic_ids: values.role === 'SME' ? values.topicIds : undefined,
      password: values.password || undefined, // Only include password if it was changed
    })
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group gap="sm" align="space-between">
          <TextInput
            required
            label="Email"
            placeholder="user@example.com"
            flex={1}
            {...form.getInputProps('email')}
            disabled={true}
          />
          {canSelectRole && (
            <Select
              required
              label="Role"
              data={[
                { value: 'User', label: 'User' },
                { value: 'SME', label: 'Expert' },
                { value: 'Admin', label: 'Program Manager' },
                // { value: 'SWI Admin', label: 'SWI Admin' },
              ]}
              {...form.getInputProps('role')}
              style={{ width: 200 }}
            />
          )}
        </Group>
        <TextInput label="Name" placeholder="John Smith" {...form.getInputProps('name')} />
        {/* <TextInput
          type="password"
          label="Password"
          placeholder="(Leave blank to keep current password)"
          style={{ flex: 1 }}
          {...form.getInputProps('password')}
        /> */}
        {showTopics && (
          <MultiSelect
            required
            label="Topics"
            placeholder="Select topics"
            data={topicOptions}
            searchable
            clearable
            comboboxProps={{ position: 'top' }}
            {...form.getInputProps('topicIds')}
          />
        )}
        <Group justify="flex-end" mt="xl">
          <Button variant="light" onClick={closeModal} disabled={isProcessing}>
            Cancel
          </Button>
          <Button type="submit" loading={isProcessing}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
